import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from '@/context/AuthContext';
import { usePropertyFilter } from '@/utils/usePropertyFilter';
import { toast } from 'react-toastify';
import TemplateRenderer from '@/components/ActionItems/TemplateRenderer';
import '@/styles/NonComplianceStyles.scss';
import { FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * NonComplianceForm - Dynamic form for creating Non-Compliance action items
 * 
 * This component renders a form for submitting non-compliance reports. It now:
 * 1. Looks up the assigned template from ModuleTemplateAssignments (ActionItems/NonCompliance)
 * 2. Uses TemplateRenderer to dynamically render form fields from the template schema
 * 3. Falls back to hardcoded fields if no template is configured
 * 
 * The form fields are now configurable via the template management system,
 * allowing administrators to customize the non-compliance form without code changes.
 */

// Context keys for module template assignment lookups
// Module key must match what's defined in PREDEFINED_CONTEXTS in ModuleTemplateConfigPage.jsx
// Non-Compliance is under the "Operations" module, not "ActionItems"
const MODULE_KEY = "Operations";
const CONTEXT_KEY = "NonCompliance";
const DEFAULT_TEMPLATE_KEY = "NonCompliance";

// Fallback options used when no template is available
const FALLBACK_NON_COMPLIANCE_OPTIONS = [
    "Deliveries",
    "Fat Traps",
    "Fire Escape Passages",
    "Fire compliance",
    "Hygiene",
    "Stacking Heights",
    "Tripping Hazard"
];

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Critical"];

/**
 * Default fallback template schema for when no template is configured in the system.
 * This provides backward compatibility with the original hardcoded form.
 */
const DEFAULT_TEMPLATE_SCHEMA = {
    fields: [
        {
            name: "shopNumber",
            label: "Shop Number",
            type: "text",
            required: true,
            placeholder: "Enter shop number"
        },
        {
            name: "tenant",
            label: "Tenant",
            type: "text",
            required: true,
            placeholder: "Enter tenant name"
        },
        {
            name: "nonCompliance",
            label: "Non-Compliance Type",
            type: "select",
            required: true,
            options: FALLBACK_NON_COMPLIANCE_OPTIONS
        },
        {
            name: "priority",
            label: "Priority",
            type: "select",
            required: true,
            options: PRIORITY_OPTIONS
        },
        {
            name: "comments",
            label: "Comments",
            type: "textarea",
            required: false,
            placeholder: "Enter any additional comments",
            rows: 3
        },
        {
            name: "tenantResponse",
            label: "Tenant Response",
            type: "text",
            required: true,
            placeholder: "Enter tenant's response"
        },
        {
            name: "tenantContactName",
            label: "Tenant Contact Name",
            type: "text",
            required: true,
            placeholder: "Enter contact person's name"
        },
        {
            name: "tenantContactNumber",
            label: "Tenant Contact Number",
            type: "text",
            required: true,
            placeholder: "Enter contact phone number",
            validation: {
                pattern: "^\\d+$",
                patternMessage: "Numbers only"
            }
        },
        {
            name: "tenantContactEmail",
            label: "Tenant Contact Email",
            type: "email",
            required: false,
            placeholder: "Enter contact email (optional)"
        }
    ],
    layout: {
        sections: [
            {
                title: "Location Details",
                fields: ["shopNumber", "tenant"]
            },
            {
                title: "Non-Compliance Information",
                fields: ["nonCompliance", "priority", "comments", "tenantResponse"]
            },
            {
                title: "Contact Information",
                fields: ["tenantContactName", "tenantContactNumber", "tenantContactEmail"]
            }
        ]
    }
};

const NonComplianceForm = () => {
    const { apiFetch, user } = useAuth();
    const { filterData, isLoading: filterLoading } = usePropertyFilter();
    
    // State for properties list
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState("");
    
    // State for template
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Ref to track if initial data has been fetched to prevent duplicate fetches
    const hasFetchedRef = useRef(false);

    /**
     * Fetch properties from API with access filtering
     */
    const fetchProperties = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success && data.data) {
                const activeProps = data.data.filter(p => p.active || p.Active);
                // Filter by user's accessible properties
                const filteredProps = filterData(activeProps, 'propertyID');
                setProperties(filteredProps);
                if (filteredProps.length === 1) {
                    setSelectedPropertyId(String(filteredProps[0].propertyID || filteredProps[0].PropertyID));
                }
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error("Error fetching properties:", e);
            toast.error("Error fetching properties");
        }
    }, [apiFetch, filterData]);

    /**
     * Fetch the assigned template for Non-Compliance action items.
     * First checks ModuleTemplateAssignments for the configured template,
     * then fetches the full template definition.
     */
    const fetchTemplate = useCallback(async () => {
        try {
            // First, get the assigned template key from module assignments using constants
            const assignmentResponse = await apiFetch(`/api/ModuleTemplateAssignments/${MODULE_KEY}/${CONTEXT_KEY}`);
            const assignmentData = await assignmentResponse.json();
            
            let templateKey = null;
            
            if (assignmentData.success && assignmentData.data?.assignedTemplateKey) {
                templateKey = assignmentData.data.assignedTemplateKey;
            }
            
            // If no template is assigned, use the default template key constant
            if (!templateKey) {
                templateKey = DEFAULT_TEMPLATE_KEY;
            }
            
            // Fetch the actual template
            const templateResponse = await apiFetch(`/api/Templates/${templateKey}`);
            const templateData = await templateResponse.json();
            
            if (templateData.success && templateData.data) {
                // Parse the schema if it's a string
                const fetchedTemplate = templateData.data;
                
                // Handle different property names (schema vs schemaJson)
                const hasSchema = fetchedTemplate.schema !== undefined && fetchedTemplate.schema !== null;
                const hasSchemaJson = fetchedTemplate.schemaJson !== undefined && fetchedTemplate.schemaJson !== null;

                // Detect and log potential data consistency issues when both properties are present
                if (hasSchema && hasSchemaJson) {
                    try {
                        const schemaAsString =
                            typeof fetchedTemplate.schema === "string"
                                ? fetchedTemplate.schema
                                : JSON.stringify(fetchedTemplate.schema);
                        const schemaJsonAsString =
                            typeof fetchedTemplate.schemaJson === "string"
                                ? fetchedTemplate.schemaJson
                                : JSON.stringify(fetchedTemplate.schemaJson);

                        if (schemaAsString !== schemaJsonAsString) {
                            // Log a warning so inconsistent template data can be investigated
                            // Note: we still prefer `schema` to avoid changing behavior,
                            // but the warning highlights that the backend/template data is inconsistent.
                            // eslint-disable-next-line no-console
                            console.warn(
                                "NonComplianceForm: Mismatch between 'schema' and 'schemaJson' for template",
                                {
                                    templateKey: fetchedTemplate.templateKey,
                                }
                            );
                        }
                    } catch (stringifyError) {
                        Sentry.captureException(stringifyError);
                        // JSON.stringify may fail if the schema objects contain circular references or BigInt values
                        // eslint-disable-next-line no-console
                        console.warn(
                            "NonComplianceForm: Unable to compare schema and schemaJson due to serialization error",
                            {
                                templateKey: fetchedTemplate.templateKey,
                                error: stringifyError
                            }
                        );
                    }
                }

                // Prefer `schema` when present to preserve existing behavior, otherwise fall back to `schemaJson`
                let schemaValue = hasSchema ? fetchedTemplate.schema : fetchedTemplate.schemaJson;
                
                if (typeof schemaValue === 'string') {
                    try {
                        schemaValue = JSON.parse(schemaValue);
                    } catch (catchError) {
                        Sentry.captureException(catchError);
                        // If parsing fails, use the default schema
                        schemaValue = DEFAULT_TEMPLATE_SCHEMA;
                    }
                }
                
                // Ensure we have the schema on the expected property
                fetchedTemplate.schema = schemaValue;
                
                // Ensure the schema has required structure (support both formats)
                if (!fetchedTemplate.schema || (!fetchedTemplate.schema.fields && !fetchedTemplate.schema.sections)) {
                    fetchedTemplate.schema = DEFAULT_TEMPLATE_SCHEMA;
                }
                
                setTemplate(fetchedTemplate);
            } else {
                // Use fallback template if none found
                setTemplate({
                    templateKey: "NonCompliance-Fallback",
                    displayName: "Non-Compliance",
                    description: "Non-compliance action item form",
                    schema: DEFAULT_TEMPLATE_SCHEMA
                });
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching template:", error);
            // Use fallback template on error
            setTemplate({
                templateKey: "NonCompliance-Fallback",
                displayName: "Non-Compliance",
                description: "Non-compliance action item form",
                schema: DEFAULT_TEMPLATE_SCHEMA
            });
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // Fetch properties and template on mount - only once when filter loading completes
    useEffect(() => {
        // Wait for property filter to load and ensure we only fetch once
        if (!filterLoading && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchProperties();
            fetchTemplate();
        }
    }, [filterLoading, fetchProperties, fetchTemplate]);

    /**
     * Handle property selection change
     */
    const handlePropertyChange = (e) => {
        setSelectedPropertyId(e.target.value);
    };

    /**
     * Handle form submission from TemplateRenderer
     * @param {Object} formData - The form data from TemplateRenderer
     */
    const handleSubmit = async (formData) => {
        if (!selectedPropertyId) {
            toast.error("Please select a property");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                PropertyID: parseInt(selectedPropertyId, 10),
                ShopNumber: formData.shopNumber || "",
                Tenant: formData.tenant || "",
                NonCompliance: formData.nonCompliance || formData.description || "",
                Comments: formData.comments || "",
                TenantResponse: formData.tenantResponse || "",
                TenantContactName: formData.tenantContactName || "",
                TenantContactNumber: formData.tenantContactNumber || "",
                TenantContactEmail: formData.tenantContactEmail || "",
                UserID: user.userID,
                Priority: formData.priority || "Low",
                // Include template key for tracking which template was used
                TemplateKey: template?.templateKey || DEFAULT_TEMPLATE_KEY
            };

            const response = await apiFetch('/api/ActionItems/non-compliance/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            
            if (data.success) {
                toast.success("Non-compliance action item created successfully");
                // Reset form by re-fetching template (this resets TemplateRenderer)
                fetchTemplate();
            } else {
                toast.error(data.message || "Failed to save non-compliance action item");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error submitting form:", error);
            toast.error("Failed to save non-compliance action item");
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="non-compliance-form loading-state">
                <div className="loading-spinner">
                    <FaSpinner className="fa-spin" />
                    <span>Loading form...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="non-compliance-form-container">
<div className="form-header">
                <h2>
                    <FaExclamationTriangle />
                    {template?.displayName || "Non-Compliance Action Item"}
                </h2>
                {template?.description && (
                    <p className="form-description">{template.description}</p>
                )}
            </div>

            {/* Property Selection - Always shown, not part of template */}
            <div className="property-selection">
                <label htmlFor="propertyID">
                    Property<span className="required-indicator">*</span>
                </label>
                <select 
                    id="propertyID"
                    name="propertyID" 
                    value={selectedPropertyId} 
                    onChange={handlePropertyChange}
                    className="form-control"
                    required
                >
                    <option value="">-- Select Property --</option>
                    {properties.map((p) => (
                        <option key={p.propertyID || p.PropertyID} value={p.propertyID || p.PropertyID}>
                            {p.propertyName || p.PropertyName}
                        </option>
                    ))}
                </select>
            </div>

            {/* Dynamic Form from Template */}
            {template && (
                <TemplateRenderer
                    template={template}
                    initialData={{
                        nonCompliance: FALLBACK_NON_COMPLIANCE_OPTIONS[0],
                        priority: "Low"
                    }}
                    onSubmit={handleSubmit}
                    submitButtonText={submitting ? "Submitting..." : "Submit Non-Compliance"}
                    readOnly={submitting}
                    externalPropertyId={selectedPropertyId}
                />
            )}
        </div>
    );
};

export default NonComplianceForm;